/**
 * Centralized Query Keys for React Query
 *
 * This file defines all query keys used across the application.
 * Consistent keys ensure proper cache deduplication and invalidation.
 *
 * Key naming convention:
 * - All keys start with a domain (e.g., 'bootstrap', 'presence', 'tasks')
 * - Hierarchical keys allow selective invalidation
 *
 * Example:
 *   queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all })  // Invalidates ALL task queries
 *   queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail('123') })  // Only specific task
 */

// =============================================================================
// Bootstrap (App Initialization)
// =============================================================================

export const bootstrapKeys = {
  all: ['bootstrap'] as const,
  data: () => [...bootstrapKeys.all, 'data'] as const,
};

// =============================================================================
// Presence & Team Status
// =============================================================================

export const presenceKeys = {
  all: ['presence'] as const,
  combined: () => [...presenceKeys.all, 'combined'] as const,
  status: () => [...presenceKeys.all, 'status'] as const,
  team: () => [...presenceKeys.all, 'team'] as const,
  blockers: () => [...presenceKeys.all, 'blockers'] as const,
  myBlockers: () => [...presenceKeys.all, 'my-blockers'] as const,
};

// =============================================================================
// User & Permissions
// =============================================================================

export const userKeys = {
  all: ['user'] as const,
  me: () => [...userKeys.all, 'me'] as const,
  permissions: () => [...userKeys.all, 'permissions'] as const,
  profile: (id: string) => [...userKeys.all, 'profile', id] as const,
};

// =============================================================================
// Notifications
// =============================================================================

export const notificationsKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationsKeys.all, 'list'] as const,
  unreadCount: () => [...notificationsKeys.all, 'unread'] as const,
  preferences: () => [...notificationsKeys.all, 'preferences'] as const,
};

// =============================================================================
// Commands & Templates
// =============================================================================

export const commandsKeys = {
  all: ['commands'] as const,
  list: () => [...commandsKeys.all, 'list'] as const,
};

export const templatesKeys = {
  all: ['templates'] as const,
  list: () => [...templatesKeys.all, 'list'] as const,
  detail: (id: string) => [...templatesKeys.all, 'detail', id] as const,
};

// =============================================================================
// Chat Sessions
// =============================================================================

export const sessionsKeys = {
  all: ['sessions'] as const,
  lists: () => [...sessionsKeys.all, 'list'] as const,
  list: (filters?: { limit?: number; offset?: number; archived?: boolean }) =>
    [...sessionsKeys.lists(), filters] as const,
  details: () => [...sessionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...sessionsKeys.details(), id] as const,
  messages: (id: string) => [...sessionsKeys.detail(id), 'messages'] as const,
  folders: () => [...sessionsKeys.all, 'folders'] as const,
};

// =============================================================================
// Tasks (existing keys from useTasksApi.ts - keep in sync)
// =============================================================================

export const tasksKeys = {
  all: ['tasks'] as const,
  lists: () => [...tasksKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...tasksKeys.lists(), filters] as const,
  details: () => [...tasksKeys.all, 'detail'] as const,
  detail: (id: string) => [...tasksKeys.details(), id] as const,
  attachments: (id: string) => [...tasksKeys.all, 'attachments', id] as const,
  comments: (id: string) => [...tasksKeys.all, 'comments', id] as const,
  assets: (id: string) => [...tasksKeys.all, 'assets', id] as const,
};

// =============================================================================
// Projects (existing keys from useProjectsApi.ts - keep in sync)
// =============================================================================

export const projectsKeys = {
  all: ['projects'] as const,
  lists: () => [...projectsKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...projectsKeys.lists(), filters] as const,
  details: () => [...projectsKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectsKeys.details(), id] as const,
  tasks: (id: string) => [...projectsKeys.detail(id), 'tasks'] as const,
  stats: () => [...projectsKeys.all, 'stats'] as const,
  myWork: () => [...projectsKeys.all, 'my-work'] as const,
  myDay: () => [...projectsKeys.all, 'my-day'] as const,
};

// =============================================================================
// Contacts/CRM (existing keys from useContactsApi.ts - keep in sync)
// =============================================================================

export const contactsKeys = {
  all: ['contacts'] as const,
  lists: () => [...contactsKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...contactsKeys.lists(), filters] as const,
  details: () => [...contactsKeys.all, 'detail'] as const,
  detail: (id: string) => [...contactsKeys.details(), id] as const,
  stats: () => [...contactsKeys.all, 'stats'] as const,
};

// =============================================================================
// Team Hub
// =============================================================================

export const teamHubKeys = {
  all: ['teamHub'] as const,
  channels: () => [...teamHubKeys.all, 'channels'] as const,
  channel: (id: string) => [...teamHubKeys.channels(), id] as const,
  messages: (channelId: string) => [...teamHubKeys.channel(channelId), 'messages'] as const,
  thread: (channelId: string, threadId: string) =>
    [...teamHubKeys.messages(channelId), 'thread', threadId] as const,
  users: () => [...teamHubKeys.all, 'users'] as const,
  invites: () => [...teamHubKeys.all, 'invites'] as const,
  pendingInvites: () => [...teamHubKeys.invites(), 'pending'] as const,
};

// =============================================================================
// Forms
// =============================================================================

export const formsKeys = {
  all: ['forms'] as const,
  lists: () => [...formsKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...formsKeys.lists(), filters] as const,
  details: () => [...formsKeys.all, 'detail'] as const,
  detail: (id: string) => [...formsKeys.details(), id] as const,
  submissions: (id: string) => [...formsKeys.detail(id), 'submissions'] as const,
};

// =============================================================================
// News Intelligence
// =============================================================================

export const newsKeys = {
  all: ['news'] as const,
  daily: (date: string) => [...newsKeys.all, 'daily', date] as const,
  dates: () => [...newsKeys.all, 'dates'] as const,
  products: () => [...newsKeys.all, 'products'] as const,
  product: (id: string) => [...newsKeys.products(), id] as const,
};

// =============================================================================
// Production
// =============================================================================

export const productionKeys = {
  all: ['production'] as const,
  weekly: () => [...productionKeys.all, 'weekly'] as const,
  weeklyDetail: (clientSlug: string, weekId: string) =>
    [...productionKeys.weekly(), clientSlug, weekId] as const,
  clients: () => [...productionKeys.all, 'clients'] as const,
};

// =============================================================================
// Customer Success
// =============================================================================

export const customerSuccessKeys = {
  all: ['customerSuccess'] as const,
  dashboard: () => [...customerSuccessKeys.all, 'dashboard'] as const,
  clients: () => [...customerSuccessKeys.all, 'clients'] as const,
  client: (id: string) => [...customerSuccessKeys.clients(), id] as const,
};

// =============================================================================
// Portal
// =============================================================================

export const portalKeys = {
  all: ['portal'] as const,
  dashboard: () => [...portalKeys.all, 'dashboard'] as const,
  deliverables: () => [...portalKeys.all, 'deliverables'] as const,
  requests: () => [...portalKeys.all, 'requests'] as const,
};

// =============================================================================
// Hiring
// =============================================================================

export const hiringKeys = {
  all: ['hiring'] as const,
  jobs: () => [...hiringKeys.all, 'jobs'] as const,
  job: (id: string) => [...hiringKeys.jobs(), id] as const,
  applicants: (jobId?: string) => [...hiringKeys.all, 'applicants', jobId] as const,
};

// =============================================================================
// QA Workflow
// =============================================================================

export const qaKeys = {
  all: ['qa'] as const,
  templates: () => [...qaKeys.all, 'templates'] as const,
  myReviews: () => [...qaKeys.all, 'my-reviews'] as const,
  taskSummary: (taskId: string) => [...qaKeys.all, 'task', taskId, 'summary'] as const,
  incidents: () => [...qaKeys.all, 'incidents'] as const,
};

// =============================================================================
// Agency Intel
// =============================================================================

export const intelKeys = {
  all: ['intel'] as const,
  feed: () => [...intelKeys.all, 'feed'] as const,
  feedFiltered: (filters: Record<string, unknown>) => [...intelKeys.feed(), filters] as const,
  pendingActions: () => [...intelKeys.all, 'pending-actions'] as const,
  pendingActionsFiltered: (filters: Record<string, unknown>) => [...intelKeys.pendingActions(), filters] as const,
  stats: () => [...intelKeys.all, 'stats'] as const,
  detail: (source: string, id: string) => [...intelKeys.all, 'detail', source, id] as const,
};

// =============================================================================
// Admin
// =============================================================================

export const adminKeys = {
  all: ['admin'] as const,
  users: () => [...adminKeys.all, 'users'] as const,
  dashboard: () => [...adminKeys.all, 'dashboard'] as const,
  analytics: (type: string) => [...adminKeys.all, 'analytics', type] as const,
};

// =============================================================================
// Huddles (Video/Audio Calls)
// =============================================================================

export const huddlesKeys = {
  all: ['huddles'] as const,
  active: () => [...huddlesKeys.all, 'active'] as const,
  teamActive: () => [...huddlesKeys.all, 'team-active'] as const,
  pendingInvites: () => [...huddlesKeys.all, 'pending-invites'] as const,
  history: (limit?: number, offset?: number) => [...huddlesKeys.all, 'history', { limit, offset }] as const,
  detail: (id: string) => [...huddlesKeys.all, 'detail', id] as const,
  transcript: (id: string) => [...huddlesKeys.detail(id), 'transcript'] as const,
};

// =============================================================================
// Unified Query Keys Export
// =============================================================================

export const queryKeys = {
  bootstrap: bootstrapKeys,
  presence: presenceKeys,
  user: userKeys,
  notifications: notificationsKeys,
  commands: commandsKeys,
  templates: templatesKeys,
  sessions: sessionsKeys,
  tasks: tasksKeys,
  projects: projectsKeys,
  contacts: contactsKeys,
  teamHub: teamHubKeys,
  forms: formsKeys,
  news: newsKeys,
  production: productionKeys,
  customerSuccess: customerSuccessKeys,
  portal: portalKeys,
  hiring: hiringKeys,
  qa: qaKeys,
  admin: adminKeys,
  intel: intelKeys,
  huddles: huddlesKeys,
};

export default queryKeys;
